import { environment } from 'src/environments/environment';

/**
* A dataservice that provides CRUD operations on transport operator service entries against the backend service
*/
export class TransportOperatorBusRouteService {
  public transportOperatorServices: Array<InterACT.Interfaces.TransportOperatorServices.TransportOperatorService> = [];
  public currentPage = 1;
  public perPageCount = 15;
  public totalCount = 0;
  public resource: InterACT.Interfaces.IResource<InterACT.Interfaces.TransportOperatorServices.TransportOperatorServiceResponse | InterACT.Interfaces.TransportOperatorServices.TransportOperatorServicesResponse>;
  public summaryResource: InterACT.Interfaces.IResource<InterACT.Interfaces.TransportOperatorServices.SummaryResponse>;
  private headers = {};

  constructor(private $resource: ng.resource.IResourceService) {
    this.resource = $resource('api/transport-operator-services/:serviceId', undefined, {
      edit: {
        method: 'PUT'
      },
      save: {
        method: 'POST',
        headers: {
          'Access-Control-Expose-Headers': 'Location'
        },
        transformResponse: (data, headers) => {
          const response: any = {};
          response.data = data;
          response.headers = headers();
          return response;
        }
      }
    });

    this.summaryResource = $resource('api/transport-operator-services/summary');
  }

  /**
   * Function to retrieve a record from the Transport Operator backend service
   *
   * @param operatorServiceId Transport Operator Service Id
   *
   * @returns An object of the transport operator service
   */
  public getTransportOperatorService(operatorServiceId: string) {
    return this.resource.get({ serviceId: operatorServiceId }).$promise.then((resource: InterACT.Interfaces.TransportOperatorServices.TransportOperatorServiceResponse) => {
      return resource.transportOperatorService;
    });
  }

  /**
   * Function to retrieve records from the Transport Operator Service backend service
   *
   * @param page The number of the page of data to be retrieved
   * @param perPageCount The number of items per page to be retrieved
   * @param searchText The search text used to build up a query string in the outgoing http request
   *
   * @returns An array of transport operator service records
   */
  public getList(page: number, perPageCount: number, searchText?: string): ng.IPromise<Array<InterACT.Interfaces.TransportOperatorServices.TransportOperatorService>> {
    const options: any = {};

    if (angular.isNumber(page) && angular.isNumber(perPageCount)) {
      options.offset = (page - 1) * perPageCount;
      options.limit = perPageCount;
      this.perPageCount = perPageCount;
      this.currentPage = page;
    } else {
      options.offset = 0;
      options.limit = 1000;
    }

    if (angular.isDefined(searchText)) {
      options.q = searchText;
    }

    return this.resource.get(options).$promise.then((resource: InterACT.Interfaces.TransportOperatorServices.TransportOperatorServicesResponse) => {
      this.transportOperatorServices = resource.transportOperatorServices;
      this.totalCount = resource.meta.totalCount;
      return resource.transportOperatorServices;
    });
  }

  /**
   * Function to edit a transport operator service
   *
   * @param serviceId The id of the service to edit
   * @param data The patch object containing the property to edit and it's value
   * @param auditReason A reason provided by the user for the edit
   *
   * @returns A promise from the resource service
   */
  public edit(serviceId: string, data: InterACT.Interfaces.TransportOperatorServices.TransportOperatorService, auditReason?: string) {
    const requestData = angular.copy(data);
    if (auditReason) {
      (<any>requestData).customHeaders = {
        auditReason: auditReason
      };
    }
    return (<any>this.resource).edit({ serviceId: serviceId }, requestData).$promise;
  }

  /**
   * Function to create a transport operator service
   *
   * @param service The object of the service to create
   *
   * @returns A promise from the resource service
   */
  public create(service: InterACT.Interfaces.TransportOperatorServices.TransportOperatorService) {
    return this.resource.save(service).$promise;
  }

  /**
   * A method that calls the summary service with the supplied searchText and returns the results
   *
   * @param query The search text used to build up a query string in the outgoing http request
   *
   * @returns A promise that resolves with a summary of all the transport operator services
   */
  public getSummary(query: string) {
    return this.summaryResource.get({ q: query }).$promise.then((resource) => {
      return resource.summary;
    });
  }
}